import { useEffect, useRef, useState } from "react";
import type { ResolvedDisplayProfile } from "./profileMatcher";
import { DISPLAY_DEBUG_DURATION_MS, isDisplayDebugEnabled } from "./profileDebug";

interface DisplayProfileDebugProps {
  resolved: ResolvedDisplayProfile;
}

export function DisplayProfileDebug({ resolved }: DisplayProfileDebugProps) {
  const enabled = isDisplayDebugEnabled({ isDevelopment: import.meta.env.DEV, search: window.location.search });
  const [visible, setVisible] = useState(enabled);
  const debugWindowStarted = useRef(false);
  const debugTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || debugWindowStarted.current || resolved.stage.width <= 0 || resolved.stage.height <= 0) {
      return;
    }
    debugWindowStarted.current = true;
    debugTimer.current = window.setTimeout(() => setVisible(false), DISPLAY_DEBUG_DURATION_MS);
  }, [enabled, resolved.stage.height, resolved.stage.width]);

  useEffect(() => () => {
    if (debugTimer.current !== null) {
      window.clearTimeout(debugTimer.current);
    }
  }, []);

  if (!visible || resolved.stage.width <= 0 || resolved.stage.height <= 0) {
    return null;
  }

  return (
    <aside className="display-profile-debug" data-testid="display-profile-debug" aria-live="polite">
      <strong>Display Profile: {resolved.profile.label}</strong>
      <span>Viewport: {resolved.stage.width}×{resolved.stage.height}</span>
      <span>Effective: {resolved.effectiveResolution.width}×{resolved.effectiveResolution.height}</span>
      <span>DPR={window.devicePixelRatio || 1} Scale={window.visualViewport?.scale || 1}</span>
      <span>Layout={resolved.profile.id} Density={(resolved.stage.width / 3840).toFixed(2)}</span>
      <span>Distance={(resolved.distance * 100).toFixed(1)}%</span>
      {resolved.warnings.map((warning) => <span className="display-profile-warning" key={warning.code}>{warning.message}</span>)}
    </aside>
  );
}
